import React, { useCallback } from 'react';
import { Input, Button, Stack, Text, ControlLink, Clickable, PasswordInput } from '@moda/om';
import ExitIcon from '@moda/icons/exit-16';
import { noop, useForm } from '@moda/portal-stanchions';
import { usePasswordValidation } from '../../hooks/usePasswordValidation';
import { useConvertGuest } from '../../hooks/useConvertGuest';
import { AuthPayloadSuccess } from '../../lib/authenticate';
import { ModalOverlay } from '../Modal';
import { hrefFor } from '../../routers';
import { useUser } from '../../hooks/useUser';

import './ConvertGuestModal.scss';

type Props = {
  onAuth?: (authUser?: AuthPayloadSuccess) => void;
  onCloseModal?: () => void;
  show: boolean;
  title: string;
};

type RegisterFormValues = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
};

const validations = {
  firstName: {
    required: {
      message: 'Please enter first name.',
      value: true
    }
  },
  lastName: {
    required: {
      message: 'Please enter last name.',
      value: true
    }
  }
};

export const ConvertGuestModal: React.FC<Props> = ({ onAuth, onCloseModal, show, title }) => {
  const {
    user: { email: userEmail }
  } = useUser();

  const { error: errorAuth, handleConvertGuest } = useConvertGuest();

  const onSubmit = useCallback(
    (values: RegisterFormValues) => {
      handleConvertGuest({
        email: values.email,
        password: values.password,
        first_name: values.firstName,
        last_name: values.lastName
      }).then(onAuth);
    },
    [handleConvertGuest, onAuth]
  );

  const { passwordValidation } = usePasswordValidation();

  const { errors: fieldErrors, handleSubmit } = useForm({
    onSubmit,
    validations: { ...validations, password: passwordValidation }
  });

  return (
    <ModalOverlay onClose={onCloseModal || noop} show={show}>
      <div className="ConvertGuestModal">
        <Clickable className="ConvertGuestModal__close" onClick={onCloseModal}>
          <ExitIcon />
        </Clickable>
        <form onSubmit={handleSubmit}>
          <Stack space={4}>
            <Text treatment="h4" family="serif">
              {title}
            </Text>
            <Text treatment="h6">
              Create an account to save your shipment and payment preferences, check your order
              status, and more.
            </Text>

            {errorAuth && <div className="ConvertGuestModal__error">{errorAuth}</div>}

            <Input defaultValue={userEmail || ''} disabled name="email" type="email" />

            <Stack direction="horizontal" space={2}>
              <Input
                className="ConvertGuestModal__input"
                error={fieldErrors.firstName?.message || Boolean(errorAuth)}
                id="firstName"
                name="firstName"
                placeholder="First Name"
                type="text"
                maxLength={50}
                focus
              />
              <Input
                className="ConvertGuestModal__input"
                error={fieldErrors.lastName?.message || Boolean(errorAuth)}
                id="lastName"
                name="lastName"
                placeholder="Last Name"
                type="text"
                maxLength={50}
              />
            </Stack>

            <PasswordInput
              error={fieldErrors.password?.message || Boolean(errorAuth)}
              focus
              name="password"
              placeholder="Please enter your password"
            />

            <Button type="submit">Save and create account</Button>

            <Text treatment="body2">
              To opt out, <ControlLink to={hrefFor.UnsubscribePage()}>unsubscribe here</ControlLink>{' '}
              or in our emails.
            </Text>
          </Stack>
        </form>
      </div>
    </ModalOverlay>
  );
};
